/**
 * Runtime configuration. Values come from Vite env vars; no secrets here.
 *
 * ВЫБОР СЕРВЕРА — РАНТАЙМОВЫЙ (Антон, 2026-09-13). Раньше homeserver вшивался
 * на этапе сборки через VITE_HOMESERVER_URL, и «потестировать на проде» означало
 * пересобрать приложение. Теперь адрес выбирается пользователем при первом входе
 * и хранится локально; env остаётся лишь значением по умолчанию.
 */
import { tr } from "./i18n";

const envHomeserverUrl = import.meta.env.VITE_HOMESERVER_URL ?? "https://matrix.algsuccess.com";
const appName = import.meta.env.VITE_APP_NAME ?? "alg(success)";

/** Готовые варианты для экрана выбора. Произвольный URL вводится там же руками. */
export const SERVER_CHOICES = [
  {
    get label() { return tr("server.main"); },
    url: "https://matrix.algsuccess.com",
    get hint() { return tr("server.hintMain"); },
  },
  {
    get label() { return tr("server.stage"); },
    url: "https://matrix1.algsuccess.com",
    get hint() { return tr("server.hintStage"); },
  },
] as const;

const SERVER_KEY = "alg.server";

/**
 * Нормализация нужна, чтобы "matrix.algsuccess.com/" и "https://matrix.algsuccess.com"
 * не давали два РАЗНЫХ префикса хранилища и, как следствие, две несвязанные сессии
 * для одного и того же сервера.
 */
export function normalizeHomeserverUrl(value: string): string {
  const raw = value.trim();
  if (!raw) throw new Error(tr("error.serverAddressRequired"));
  const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    throw new Error(tr("error.serverAddressInvalid"));
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") {
    throw new Error(tr("error.serverAddressHttps"));
  }
  return `${url.protocol}//${url.host}`;
}

/** null — сервер ещё не выбран; App показывает экран выбора вместо входа. */
export function selectedHomeserverUrl(): string | null {
  const raw = localStorage.getItem(SERVER_KEY);
  if (!raw) return null;
  try {
    return normalizeHomeserverUrl(raw);
  } catch {
    localStorage.removeItem(SERVER_KEY);
    return null;
  }
}

export function setHomeserverUrl(value: string): string {
  const normalized = normalizeHomeserverUrl(value);
  localStorage.setItem(SERVER_KEY, normalized);
  return normalized;
}

/**
 * Сброс выбора — возврат к экрану серверов. Сессии НЕ трогаем: они лежат под
 * префиксом своего хоста, и вернувшись на прежний сервер, пользователь должен
 * попасть внутрь без повторного логина.
 */
export function clearHomeserverChoice(): void {
  localStorage.removeItem(SERVER_KEY);
}

/**
 * Хост — часть ключа хранилища.
 *
 * ПОЧЕМУ ЭТО ОБЯЗАТЕЛЬНО: client_id выдаётся конкретным MAS динамической
 * регистрацией (RFC 7591), а access-токен и device_id принадлежат конкретному
 * серверу. Общий ключ на два сервера означал бы, что прод получает client_id,
 * выданный стендом, — вход ломается неочевидно, с видом «приложение сломалось».
 */
export function serverStoragePrefix(url: string = config.homeserverUrl): string {
  return `alg.${new URL(url).host}`;
}

/** Короткое имя сервера для индикатора в интерфейсе. */
export function serverLabel(url: string = config.homeserverUrl): string {
  const known = SERVER_CHOICES.find((s) => s.url === url);
  if (known) return known.label;
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/**
 * ЗАПУСК ПОД CAPACITOR-ОБОЛОЧКОЙ (Антон/Момо, 2026-09-20 19:08 UTC).
 *
 * Проблема: в Android WebView origin приложения — http(s)://localhost,
 * а дефолтная политика MAS для application_type "web" ЗАПРЕЩАЕТ
 * localhost в redirect_uri/client_uri → динамическая регистрация клиента
 * отклоняется ("invalid redirect_uri, invalid client_uri"). Для
 * application_type "native" MAS, наоборот, РАЗРЕШАЕТ http://localhost.
 *
 * Признаки нативной оболочки (достаточно одного):
 *   1. window.Capacitor?.isNativePlatform?.() === true — официальный API;
 *   2. origin http(s)://localhost — у обычного браузера на devapp origin
 *      https://devapp.algsuccess.com, localhost бывает только в WebView
 *      (и у локальной разработки vite — там тоже логичен native-профиль,
 *      но регистрация идёт на devapp MAS; для dev-сервера это ок: вход
 *      в браузере на localhost:5173 тоже считается native-shell и
 *      регистрируется с client_uri devapp — см. постановку).
 *
 * ВАЖНО про оболочку (делает Момо, НЕ этот репозиторий): в
 * capacitor.config.json обязателен server.androidScheme = "http", иначе
 * origin будет https://localhost, который MAS отвергает и для native.
 */
interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
}

export function isNativeShell(): boolean {
  if (typeof window === "undefined") return false;
  const cap = (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
  try {
    if (cap?.isNativePlatform?.() === true) return true;
  } catch {
    /* Capacitor есть, но вызов упал — считаем по origin, как ниже. */
  }
  return /^https?:\/\/localhost(?::\d+)?$/.test(window.location.origin);
}

export const config = {
  /** Геттер, а не константа: значение меняется без перезагрузки модуля. */
  get homeserverUrl(): string {
    return selectedHomeserverUrl() ?? envHomeserverUrl;
  },
  appName,
  /**
   * client_uri для динамической регистрации OIDC (RFC 7591).
   *
   * В браузере на devapp совпадает с origin (прежнее поведение, см.
   * appUri ниже). Под Capacitor origin — http://localhost, который MAS
   * отверг бы и как client_uri, поэтому здесь — публичный адрес
   * приложения. Переопределяется через VITE_APP_CLIENT_URI.
   */
  get appClientUri(): string {
    return import.meta.env.VITE_APP_CLIENT_URI ?? "https://devapp.algsuccess.com";
  },
  /**
   * Лимит устройств на аккаунт — ИНТЕРФЕЙСНАЯ настройка (Антон,
   * 2026-09-12: «лимит одной настройкой, цифру меняет Антон»).
   *
   * ВАЖНО: это только число для показа в кабинете. Фактическое
   * ограничение («шестое и далее — запрос на подтверждение админа»)
   * обязан резать СЕРВЕР (MAS/Synapse policy) — клиентская проверка
   * была бы театром: её обходит любой curl. Пока серверной части нет,
   * раздел «Устройства» честно пишет, что ограничение ещё не действует.
   */
  deviceLimit: Number(import.meta.env.VITE_DEVICE_LIMIT ?? 5) || 5,
  /** Where MAS sends the user back. Must match the registered redirect_uri exactly. */
  get redirectUri(): string {
    return window.location.origin + window.location.pathname;
  },
  get appUri(): string {
    return window.location.origin;
  },
} as const;
